
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useWidgetPolls } from '@/hooks/polls/useWidgetPolls';
import { useCountry } from '@/contexts/country';
import PollWidgetEmpty from './widget/PollWidgetEmpty';
import PollWidgetNavigation from './widget/PollWidgetNavigation';
import PollsLoadingState from './PollsLoadingState';
import { useAuth } from '@/contexts/auth';
import AuthNoticeBar from './widget/AuthNoticeBar';
import { useVotePermissions } from '@/hooks/polls/useVotePermissions';
import { usePollVotesRealtime } from '@/hooks/polls/usePollVotesRealtime';
import UniversalPollDisplay from './shared/UniversalPollDisplay';
import PollDetailsLink from './widget/PollDetailsLink';
import { Link } from 'react-router-dom';

const PollWidget = () => {
  const { countryCode } = useCountry();
  const { user, isAuthenticated } = useAuth();
  const { canVote, cannotVoteReason } = useVotePermissions();
  const [error, setError] = useState<string | undefined>();
  
  const {
    polls,
    setPolls,
    currentPollIndex,
    setCurrentPollIndex,
    isLoading,
    fetchError,
    userVotes,
    setUserVotes,
    nextPoll,
    prevPoll,
    refresh
  } = useWidgetPolls(countryCode);

  // Set up the real-time subscription for poll votes with proper state management
  const { markRecentVote } = usePollVotesRealtime(polls, setPolls, userVotes, setUserVotes);

  const currentPoll = polls[currentPollIndex];

  // Reset error when polls or index changes
  useEffect(() => {
    setError(undefined);
  }, [polls, currentPollIndex]);

  // Set error from fetch error if it exists
  useEffect(() => {
    if (fetchError) {
      setError(`Failed to load polls: ${fetchError.message}`);
    }
  }, [fetchError]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Participate in Active Polls</CardTitle>
        </CardHeader>
        <CardContent>
          <PollsLoadingState />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-red-600">{error}</div>
        </CardContent>
      </Card>
    );
  }

  // Determine the best URL for the poll
  const pollUrl = currentPoll?.poll_number ? 
    `/polls/${currentPoll.poll_number}` : 
    `/poll/${currentPoll?.id}`;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Participate in Active Polls</CardTitle>
        {!isAuthenticated && <AuthNoticeBar user={user} canVote={!!user && canVote(currentPoll)} />}
      </CardHeader>
      <CardContent>
        {polls.length === 0 ? (
          <PollWidgetEmpty message="No active polls available at this time." />
        ) : (
          <>
            {/* Add poll title as a heading */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-nation-blue dark:text-blue-300 flex-1">
                <Link to={pollUrl} className="hover:underline">
                  {currentPoll.title}
                </Link>
              </h3>
            </div>
            
            <UniversalPollDisplay
              polls={polls}
              setPolls={setPolls}
              userVotes={userVotes}
              setUserVotes={setUserVotes}
              currentPoll={currentPoll}
              markRecentVote={markRecentVote}
              onRefresh={refresh}
              variant="compact"
              showShareButtons={false}
              cannotVoteReason={cannotVoteReason(currentPoll)}
            />
            
            <PollDetailsLink url={pollUrl} pollId={currentPoll.id} />
            
            {polls.length > 1 && (
              <PollWidgetNavigation
                currentIndex={currentPollIndex}
                totalPolls={polls.length}
                onNext={nextPoll}
                onPrevious={prevPoll}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PollWidget;
